import { inject } from '@angular/core';
import { CanActivateChildFn, Router, type RouterStateSnapshot } from '@angular/router';

import { AuthStore } from './auth-store';
import { OnboardingStore } from '../data/onboarding-store';
import { shouldShowOnboarding } from '../data/onboarding-copy';

/** The tour's own route, which obviously must not be rerouted to itself. */
const TOUR_PATH = '/onboarding';

/**
 * Send an admin who has not been through the welcome tour to it, once.
 *
 * Runs as the shell's `canActivateChild`, after {@link adminGuard} has already
 * established that there IS an active staff member - so this only has to
 * answer "have they seen it", and the answer comes from the server (their
 * `blumeStaff/{uid}.onboardedAt`), not from this browser.
 *
 * ## It fails OPEN, deliberately
 *
 * If the state cannot be fetched - the callable is cold, deploying, or the
 * network is down - `loaded` stays false and this returns true. The direction
 * matters: a false negative shows the tour again to someone who has seen it,
 * which Settings can fix and which is mildly annoying. A false POSITIVE would
 * bounce an admin to the tour on every navigation with no way through, because
 * the same failure that lost their stamp will lose the one they write on the
 * way out. Locking the team out of the console over a welcome screen is not a
 * trade worth making.
 *
 * ## Why the redirect carries `next`
 *
 * Losing someone's destination is the difference between a reroute and an
 * interruption: a link into a partner, clicked from Slack on their first day,
 * should still arrive after the tour rather than dumping them on the
 * dashboards wondering what happened to it.
 */
export const onboardingGuard: CanActivateChildFn = async (_route, state: RouterStateSnapshot) => {
  // EVERY inject() first, before the first await. An injection context does not
  // survive one - resuming after `await whenReady()` and injecting there throws
  // NG0203, which is a guard that rejects every navigation rather than a test
  // that fails. Cheap to get right, silent to get wrong.
  const router = inject(Router);
  const auth = inject(AuthStore);
  const store = inject(OnboardingStore);

  // The tour itself, and anything under it, is exempt - otherwise this reroutes
  // the route it is rerouting TO, which is a navigation loop rather than a
  // welcome.
  if (state.url.split('?')[0] === TOUR_PATH) return true;

  await auth.whenReady();
  // adminGuard has the final say on non-staff; this one just declines to have
  // an opinion about them.
  if (!auth.isStaff()) return true;

  await store.load();

  const show = shouldShowOnboarding({
    onboardedAt: store.onboardedAt(),
    loaded: store.loaded(),
    isStaff: auth.isStaff(),
  });
  if (!show) return true;

  return router.createUrlTree([TOUR_PATH], { queryParams: { next: state.url } });
};
